import React from "react";
import PropTypes from "prop-types";
import BaseBlock from "./BaseBlock";
import { renderNarrative, hasFigToken } from "../metrics/renderNarrative";

/**
 * ExecNarrativeBlock - executive narrative summary for the board report.
 *
 * Renders from an injected `narrative` prop (run.narrative) - not fetched.
 * models.BoardReportRun.Narrative is a free-form map[string]interface{} on
 * the backend, serialized snake_case (Go's default JSON marshaling of a
 * map[string]interface{} keeps whatever keys were set), so this component
 * assumes a documented binding-default shape:
 *   { bullets: string[], what_changed: string, why: string, what_to_do: string }
 *
 * SP6: any field above may contain `{{fig:KEY}}` @figure tokens, rendered live via
 * renderNarrative from the optional `metrics` prop. When ANY rendered field contains a token,
 * `BaseBlock`'s `isLoading` is gated on `metricsLoading` so the PDF poller waits; a token-free
 * narrative keeps `isLoading={false}` (unchanged).
 */
const ExecNarrativeBlock = ({
  title = "Executive summary",
  narrative,
  metrics,
  metricsLoading = false,
}) => {
  const bullets = narrative?.bullets ?? [];
  const whatChanged = narrative?.what_changed;
  const why = narrative?.why;
  const whatToDo = narrative?.what_to_do;

  const isLoading =
    (bullets.some(hasFigToken) ||
      hasFigToken(whatChanged) ||
      hasFigToken(why) ||
      hasFigToken(whatToDo)) &&
    metricsLoading;

  return (
    <BaseBlock title={title} isLoading={isLoading} error={null}>
      {bullets.length > 0 && (
        <ul className="a4-narrative-bullets">
          {bullets.map((bullet, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{renderNarrative(bullet, metrics)}</li>
          ))}
        </ul>
      )}
      <dl className="a4-narrative-sections">
        <dt>What changed</dt>
        <dd>{whatChanged ? renderNarrative(whatChanged, metrics) : "—"}</dd>
        <dt>Why</dt>
        <dd>{why ? renderNarrative(why, metrics) : "—"}</dd>
        <dt>What to do</dt>
        <dd>{whatToDo ? renderNarrative(whatToDo, metrics) : "—"}</dd>
      </dl>
    </BaseBlock>
  );
};

ExecNarrativeBlock.propTypes = {
  title: PropTypes.string,
  narrative: PropTypes.shape({
    bullets: PropTypes.arrayOf(PropTypes.string),
    what_changed: PropTypes.string,
    why: PropTypes.string,
    what_to_do: PropTypes.string,
  }),
  metrics: PropTypes.object,
  metricsLoading: PropTypes.bool,
};

export default ExecNarrativeBlock;
